import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const PLATFORMS = ["Chrome / Edge Extension", "Windows 11 Desktop App", "iOS", "Android", "WebApp", "All"];

export default function UpdateLogPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [updates, setUpdates] = useState([]);
  const [versions, setVersions] = useState({});
  const [filterPlatform, setFilterPlatform] = useState("All Platforms");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ platform: "All", notes: "", admin_name: "" });
  const [loading, setLoading] = useState(false);

  const login = async () => {
    try {
      await axios.post(`${API}/admin/verify-password`, { password });
      setAuthed(true);
      toast.success("Access granted");
    } catch { toast.error("Invalid password"); }
  };

  const fetchUpdates = async () => {
    try {
      const [uRes, vRes] = await Promise.all([
        axios.get(`${API}/admin/updates`, { params: filterPlatform !== "All Platforms" ? { platform: filterPlatform } : {} }),
        axios.get(`${API}/admin/updates/versions`)
      ]);
      setUpdates(uRes.data.items);
      setVersions(vRes.data.versions);
    } catch { toast.error("Failed to load updates"); }
  };

  useEffect(() => { if (authed) fetchUpdates(); }, [authed, filterPlatform]);

  const createUpdate = async () => {
    if (!form.admin_name.trim()) { toast.error("Admin name required"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/updates`, form);
      toast.success("Update logged");
      setShowForm(false);
      setForm({ platform: "All", notes: "", admin_name: form.admin_name });
      fetchUpdates();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  const deleteUpdate = async (id) => {
    if (!window.confirm("Delete this update entry?")) return;
    try {
      await axios.delete(`${API}/admin/updates/${id}`);
      toast.success("Deleted");
      fetchUpdates();
    } catch { toast.error("Failed to delete"); }
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const platformColor = (p) => {
    const map = { "Chrome / Edge Extension": "#4ade80", "Windows 11 Desktop App": "#60a5fa", "iOS": "#f472b6", "Android": "#a78bfa", "WebApp": "#facc15", "All": "#A3E635" };
    return map[p] || "#E8E4DC";
  };

  if (!authed) {
    return (
      <Layout>
        <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center px-4" data-testid="update-log-auth">
          <div className="card-glass p-8 w-full max-w-sm" style={{ background: "rgba(0,0,0,0.9)" }}>
            <h1 className="font-heading text-2xl font-bold text-white mb-2 text-center">Update Log</h1>
            <p className="text-[#E8E4DC] text-sm text-center mb-6">Admin access required</p>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()}
              className="input-dark w-full mb-4" placeholder="Admin password" data-testid="update-log-password" />
            <button onClick={login} className="btn-primary w-full" data-testid="update-log-submit">Authenticate</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] py-16 px-4" data-testid="update-log-page">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white neon-text-white">Update Log</h1>
              <p className="text-[#E8E4DC] text-sm mt-1">Platform updates and version history</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => navigate("/install")} className="btn-secondary text-sm" data-testid="back-to-install">Back to Install</button>
              <button onClick={() => setShowForm(true)} className="btn-primary text-sm" data-testid="new-update-btn">+ Log Update</button>
            </div>
          </div>

          {/* Version badges */}
          <div className="flex flex-wrap gap-3 mb-8">
            {PLATFORMS.filter(p => p !== "All").map(p => (
              <div key={p} className="card-glass px-4 py-2 rounded-full flex items-center gap-2" data-testid={`version-badge-${p}`}>
                <div className="w-2 h-2 rounded-full" style={{ background: platformColor(p) }} />
                <span className="text-[#E8E4DC] text-xs">{p}</span>
                <span className="text-white text-xs font-mono font-bold">v{versions[p] || "0.0.0"}</span>
              </div>
            ))}
          </div>

          {/* Filter */}
          <div className="mb-6">
            <select value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)} className="input-dark" data-testid="update-filter">
              <option value="All Platforms">All Platforms</option>
              {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {/* Table */}
          <div className="card-glass overflow-hidden rounded-xl" style={{ background: "rgba(0,0,0,0.6)" }}>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="update-log-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-[#E8E4DC] text-xs uppercase tracking-wider font-semibold">Date / Time</th>
                    <th className="text-left p-4 text-[#E8E4DC] text-xs uppercase tracking-wider font-semibold">Platform</th>
                    <th className="text-left p-4 text-[#E8E4DC] text-xs uppercase tracking-wider font-semibold">Version</th>
                    <th className="text-left p-4 text-[#E8E4DC] text-xs uppercase tracking-wider font-semibold">Admin</th>
                    <th className="text-left p-4 text-[#E8E4DC] text-xs uppercase tracking-wider font-semibold">Notes</th>
                    <th className="text-right p-4 text-[#E8E4DC] text-xs uppercase tracking-wider font-semibold w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {updates.length === 0 ? (
                    <tr><td colSpan="6" className="text-center text-[#E8E4DC] py-12">No updates recorded yet</td></tr>
                  ) : updates.map((u) => (
                    <tr key={u.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`update-row-${u.id}`}>
                      <td className="p-4 text-white text-sm whitespace-nowrap">{fmtDate(u.created_at)}</td>
                      <td className="p-4">
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium px-2.5 py-1 rounded-full" style={{ background: `${platformColor(u.platform)}15`, color: platformColor(u.platform) }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: platformColor(u.platform) }} />
                          {u.platform}
                        </span>
                      </td>
                      <td className="p-4 text-white text-sm font-mono">v{u.version}</td>
                      <td className="p-4 text-[#E8E4DC] text-sm">{u.admin_name}</td>
                      <td className="p-4 text-[#E8E4DC] text-sm max-w-xs truncate">{u.notes || "—"}</td>
                      <td className="p-4 text-right">
                        <button onClick={() => deleteUpdate(u.id)} className="text-red-400/60 hover:text-red-400 transition-colors p-1" title="Delete" data-testid={`delete-update-${u.id}`}>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* New Update Modal */}
          {showForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm" data-testid="new-update-modal">
              <div className="card-glass p-6 w-full max-w-md" style={{ background: "rgba(0,0,0,0.95)" }}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold text-white">Log New Update</h2>
                  <button onClick={() => setShowForm(false)} className="text-[#E8E4DC] hover:text-white">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-[#E8E4DC] text-xs mb-1 block">Platform</label>
                    <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="input-dark w-full" data-testid="update-platform">
                      {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#E8E4DC] text-xs mb-1 block">Version (auto-generated)</label>
                    <div className="input-dark w-full text-[#E8E4DC]/60 cursor-not-allowed">
                      Next: v{((versions[form.platform] || "0.0.0").split(".")[0] * 1 + 1)}.0.0
                    </div>
                  </div>
                  <div>
                    <label className="text-[#E8E4DC] text-xs mb-1 block">Administrator Name *</label>
                    <input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="input-dark w-full" placeholder="Your name" data-testid="update-admin" />
                  </div>
                  <div>
                    <label className="text-[#E8E4DC] text-xs mb-1 block">Notes</label>
                    <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-dark w-full h-24 resize-none" placeholder="What changed in this update..." data-testid="update-notes" />
                  </div>
                </div>
                <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
                  <button onClick={() => setShowForm(false)} className="btn-secondary flex-1">Cancel</button>
                  <button onClick={createUpdate} disabled={loading} className="btn-primary flex-1" data-testid="submit-update">
                    {loading ? "Saving..." : "Log Update"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
